import React, { useState } from 'react';

const ACCOUNTS = [
  'Cash', 'Accounts Receivable', 'Inventory', 'Fixed Assets',
  'Accounts Payable', 'Short-term Loans', 'Owner Equity',
  'Sales Revenue', 'Service Revenue', 'Cost of Goods Sold',
  'Salaries Expense', 'Rent Expense',
];

const SAMPLE_ENTRIES = [
  { id: 'JE-001', date: '02/01/2026 08:00 AM', ref: 'INV-001', description: 'Sales to customer', debit: 5000, credit: 5000, status: 'Posted' },
  { id: 'JE-002', date: '02/03/2026 10:30 AM', ref: 'PO-012', description: 'Purchase of inventory', debit: 2300, credit: 2300, status: 'Posted' },
  { id: 'JE-003', date: '02/10/2026 02:00 PM', ref: 'EXP-007', description: 'Monthly rent payment', debit: 1500, credit: 1500, status: 'Draft' },
];

const DEFAULT_ROWS = 10;
const emptyLine = () => ({ account: '', debit: '', credit: '' });
const makeLines = () => Array.from({ length: DEFAULT_ROWS }, emptyLine);

const nowString = () => {
  const d = new Date();
  return d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

const JournalEntry = () => {
  const [entries, setEntries] = useState(SAMPLE_ENTRIES);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [lines, setLines] = useState(makeLines());
  const [header, setHeader] = useState({ ref: '', date: nowString(), notes: '' });

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.001 && totalDebit > 0;

  const updateLine = (i, field, val) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));

  const openModal = () => {
    setLines(makeLines());
    setHeader({ ref: '', date: nowString(), notes: '' });
    setShowModal(true);
  };

  const handleSave = () => {
    const usedLines = lines.filter(l => l.account);
    if (!usedLines.length) return;
    const newEntry = {
      id: `JE-${String(entries.length + 1).padStart(3, '0')}`,
      date: header.date,
      ref: header.ref || '-',
      description: header.notes || usedLines[0].account,
      debit: totalDebit,
      credit: totalCredit,
      status: balanced ? 'Posted' : 'Draft',
    };
    setEntries(prev => [newEntry, ...prev]);
    setShowModal(false);
  };

  const filtered = entries.filter(e =>
    e.description.toLowerCase().includes(search.toLowerCase()) || e.id.includes(search)
  );

  return (
    <div className="accounting-page">
      <div className="table-controls">
        <div className="table-controls-left">
        </div>
        <div className="table-controls-right">
          <div className="tabs-search-wrapper">
            <input
              className="tabs-search-input"
              placeholder="Search entries..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="control-btn-primary" onClick={openModal}>
            <span className="plus-icon">+</span> New Entry
          </button>
        </div>
      </div>

      <div className="acc-stats-row">
        {[
          { label: 'Total Entries', value: entries.length, color: 'blue' },
          { label: 'Posted', value: entries.filter(e => e.status === 'Posted').length, color: 'green' },
          { label: 'Drafts', value: entries.filter(e => e.status === 'Draft').length, color: 'orange' },
          { label: 'Total Debits', value: `$${entries.reduce((s, e) => s + e.debit, 0).toLocaleString()}`, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`}>
            <div className="acc-stat-label">{s.label}</div>
            <div className="acc-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="table-content-card">
        <h3 className="acc-card-title">Journal Entries</h3>
        <div className="acc-table-wrapper products-table-container">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Entry #</th>
                <th>Date</th>
                <th>Reference</th>
                <th>Description</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id}>
                  <td><span style={{ fontWeight: 600, color: 'var(--etpos-primary,#6366f1)' }}>{e.id}</span></td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{e.date}</td>
                  <td>{e.ref}</td>
                  <td>{e.description}</td>
                  <td className="text-right">${e.debit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="text-right">${e.credit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="text-center">
                    <span className={`acc-badge ${e.status === 'Posted' ? 'acc-badge-green' : 'acc-badge-yellow'}`}>{e.status}</span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7}><div className="acc-empty"><div className="acc-empty-icon">📒</div><h4>No entries found</h4></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add Journal Entry Modal ── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '8px', width: '95%', maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}
          >
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>Add Journal Entry</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {/* Header fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Reference No</label>
                  <input
                    value={header.ref}
                    onChange={e => setHeader(p => ({ ...p, ref: e.target.value }))}
                    placeholder="e.g. INV-001"
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Journal Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    value={header.date}
                    onChange={e => setHeader(p => ({ ...p, date: e.target.value }))}
                    placeholder="MM/DD/YYYY HH:MM AM"
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Additional Notes</label>
                  <textarea
                    value={header.notes}
                    onChange={e => setHeader(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.875rem' }}
                  />
                </div>
              </div>

              {/* Lines table */}
              <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', background: '#f8fafc' }}>
                  <colgroup>
                    <col style={{ width: 44 }} />
                    <col />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>#</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Account</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Debit</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody style={{ background: '#fff' }}>
                    {lines.map((l, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: 'center', padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 500 }}>{i + 1}</td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <select
                            value={l.account}
                            onChange={e => updateLine(i, 'account', e.target.value)}
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', background: '#fff', fontSize: '0.85rem' }}
                          >
                            <option value="">Please select</option>
                            {ACCOUNTS.map(a => <option key={a}>{a}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <input
                            type="number"
                            min="0"
                            value={l.debit}
                            onChange={e => updateLine(i, 'debit', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <input
                            type="number"
                            min="0"
                            value={l.credit}
                            onChange={e => updateLine(i, 'credit', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', fontSize: '0.85rem' }}
                          />
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr>
                      <td colSpan={2} style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: '#1e293b' }}>Total</td>
                      <td style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: balanced ? '#10b981' : totalDebit > 0 ? '#ef4444' : '#1e293b' }}>
                        {totalDebit > 0 ? `$${totalDebit.toFixed(2)}` : ''}
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: balanced ? '#10b981' : totalCredit > 0 ? '#ef4444' : '#1e293b' }}>
                        {totalCredit > 0 ? `$${totalCredit.toFixed(2)}` : ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!balanced && totalDebit > 0 && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#ef4444', fontSize: '0.85rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠ Not balanced — Difference: ${Math.abs(totalDebit - totalCredit).toFixed(2)}
                </div>
              )}

              <button
                onClick={() => setLines(prev => [...prev, emptyLine()])}
                style={{ marginTop: '1rem', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '6px', padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.875rem', color: '#64748b', width: '100%', fontWeight: 600, transition: 'all 0.15s ease' }}
                onMouseEnter={e => { e.target.style.borderColor = '#94a3b8'; e.target.style.color = '#475569'; e.target.style.background = '#f1f5f9'; }}
                onMouseLeave={e => { e.target.style.borderColor = '#cbd5e1'; e.target.style.color = '#64748b'; e.target.style.background = '#f8fafc'; }}
              >
                + Add Row
              </button>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: '#f8fafc' }}>
              <button
                onClick={handleSave}
                style={{ background: '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 2.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
                onMouseEnter={e => e.target.style.background = '#4c1d95'}
                onMouseLeave={e => e.target.style.background = '#5b21b6'}
              >
                Save
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 2.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#334155'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntry;
